import * as Yup from 'yup'

/** Letters + combining marks (Marathi matras/virama), with optional spaces / . / ' / - */
const PERSON_NAME_PATTERN = /^[\p{L}][\p{L}\p{M}\s.'’-]*$/u
/** English display name / slug-friendly value */
const ENGLISH_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9\s.'’-]*$/
/** Letters, combining marks, and numbers; spaces allowed (experience only) */
const EXPERIENCE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}\p{M}\s]*$/u
/** Requires local@domain.tld (rejects incomplete values like ritu@y). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const MIN_PASSWORD_LENGTH = 6
/** General password max (admin user/author/ad-manager forms/forgot password + shared validators). */
export const MAX_PASSWORD_LENGTH = 20
/** Forgot-password / reset-password UI + API only. */
export const FORGOT_PASSWORD_MAX_LENGTH = 20
export const MAX_TEXT_LENGTH = 2000
export const MAX_SHORT_TEXT_LENGTH = 255
/** Person name cap for account forms; well within users.name VARCHAR(255). */
export const MAX_PERSON_NAME_LENGTH = 50
/** English display name cap for account forms. */
export const MAX_ENGLISH_NAME_LENGTH = 50
/** Designation / experience caps (within users VARCHAR columns). */
export const MAX_DESIGNATION_LENGTH = 50
export const MAX_EXPERIENCE_LENGTH = 50

export const ACCOUNT_FIELD_ERRORS = {
	nameRequired: 'Name is required',
	nameFormat:
		'Name must include letters (Marathi or English) and cannot be only numbers or special characters',
	nameTooLong: `Name must be ${MAX_PERSON_NAME_LENGTH} characters or fewer`,
	nameEnglishRequired: 'English name is required',
	nameEnglishTooLong: `English Name must be ${MAX_ENGLISH_NAME_LENGTH} characters or fewer`,
	nameEnglishFormat:
		'English name must start with a letter and contain only English letters, numbers, spaces, hyphens, apostrophes or periods',
	emailRequired: 'Email is required',
	emailFormat: 'Enter a valid email address',
	passwordRequired: 'Password is required',
	passwordMinLength: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
	passwordMaxLength: `Password must be at most ${MAX_PASSWORD_LENGTH} characters`,
	forgotPasswordMaxLength: `Password must be at most ${FORGOT_PASSWORD_MAX_LENGTH} characters`,
	designationRequired: 'Designation is required',
	designationFormat:
		'Designation must include letters and cannot contain only numbers or special characters',
	designationTooLong: `Designation cannot exceed ${MAX_DESIGNATION_LENGTH} characters.`,
	experienceRequired: 'Experience is required',
	experienceFormat: 'Experience can contain only letters and numbers',
	experienceTooLong: `Experience cannot exceed ${MAX_EXPERIENCE_LENGTH} characters.`,
	bioRequired: 'Bio / description is required',
	bioFormat:
		'Bio must include letters (Marathi or English) and cannot be only numbers or special characters',
	urlFormat: 'Enter a valid URL starting with http:// or https://',
	twitterFormat: 'Enter a valid Twitter/X profile URL like https://x.com/username',
	linkedinFormat: 'Enter a valid LinkedIn profile URL like https://www.linkedin.com/in/username',
}

// Backwards-compatible aliases used by older call sites
export const PERSON_NAME_ERROR = ACCOUNT_FIELD_ERRORS.nameFormat
export const EMAIL_FORMAT_ERROR = ACCOUNT_FIELD_ERRORS.emailFormat
export const PASSWORD_REQUIRED_ERROR = ACCOUNT_FIELD_ERRORS.passwordRequired
export const PASSWORD_MIN_LENGTH_ERROR = ACCOUNT_FIELD_ERRORS.passwordMinLength

export function normalizePersonName(value) {
	return String(value ?? '').trim()
}

export function normalizeEnglishName(value) {
	return String(value ?? '').trim()
}

export function isValidPersonName(value) {
	const name = normalizePersonName(value)
	if (!name) return false
	return PERSON_NAME_PATTERN.test(name)
}

export function isValidEnglishName(value) {
	const name = normalizeEnglishName(value)
	if (!name || name.length > MAX_ENGLISH_NAME_LENGTH) return false
	return ENGLISH_NAME_PATTERN.test(name)
}

export function normalizeEmail(value) {
	return String(value ?? '').trim().toLowerCase()
}

export function isValidEmail(value) {
	const email = normalizeEmail(value)
	if (!email || email.length > 255) return false
	if (!EMAIL_PATTERN.test(email)) return false

	const atIndex = email.lastIndexOf('@')
	if (atIndex <= 0 || atIndex === email.length - 1) return false

	const local = email.slice(0, atIndex)
	const domain = email.slice(atIndex + 1)

	if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false
	if (/[^\w.+-]/.test(local)) return false

	if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false
	if (!domain.includes('.')) return false

	return true
}

export function isValidPassword(value, { required = false } = {}) {
	const password = String(value ?? '')
	if (!password) return !required
	return (
		password.length >= MIN_PASSWORD_LENGTH &&
		password.length <= MAX_PASSWORD_LENGTH
	)
}

/** Returns a password validation error message, or null when valid. */
export function getPasswordValidationError(
	value,
	{ required = false, maxLength = MAX_PASSWORD_LENGTH } = {}
) {
	const password = String(value ?? '')
	if (!password) {
		return required ? ACCOUNT_FIELD_ERRORS.passwordRequired : null
	}
	if (password.length < MIN_PASSWORD_LENGTH) {
		return ACCOUNT_FIELD_ERRORS.passwordMinLength
	}
	if (password.length > maxLength) {
		return maxLength === FORGOT_PASSWORD_MAX_LENGTH
			? ACCOUNT_FIELD_ERRORS.forgotPasswordMaxLength
			: ACCOUNT_FIELD_ERRORS.passwordMaxLength
	}
	return null
}

export function normalizeText(value) {
	return String(value ?? '').trim()
}

/** Must include at least one letter (Marathi or English). Rejects numbers-only / specials-only. */
export function isValidTextContent(value, { min = 1, max = MAX_TEXT_LENGTH } = {}) {
	const text = normalizeText(value)
	if (text.length < min || text.length > max) return false
	return /\p{L}/u.test(text)
}

export function isValidExperience(value) {
	const text = normalizeText(value)
	if (!text) return false
	return EXPERIENCE_PATTERN.test(text)
}

export function normalizeUrl(value) {
	return String(value ?? '').trim()
}

export function isValidHttpUrl(value) {
	const raw = normalizeUrl(value)
	if (!raw) return false
	try {
		const url = new URL(raw)
		return url.protocol === 'http:' || url.protocol === 'https:'
	} catch {
		return false
	}
}

/** Strict https-only URL (preferred for public profile links). */
export function isValidHttpsUrl(value) {
	return Boolean(parseHttpsUrl(value))
}

function parseHttpsUrl(value) {
	const raw = normalizeUrl(value)
	if (!raw) return null
	if (/\s/.test(raw)) return null
	try {
		const url = new URL(raw)
		if (url.protocol !== 'https:') return null
		if (url.username || url.password) return null
		return url
	} catch {
		return null
	}
}

const TWITTER_RESERVED_HANDLES = new Set([
	'home',
	'explore',
	'search',
	'i',
	'intent',
	'share',
	'settings',
	'messages',
	'notifications',
	'compose',
	'login',
	'signup',
	'privacy',
	'tos',
])

/** https://x.com/username or https://twitter.com/username */
export function isValidTwitterProfileUrl(value) {
	const url = parseHttpsUrl(value)
	if (!url) return false

	const host = url.hostname.replace(/^www\./i, '').toLowerCase()
	if (host !== 'x.com' && host !== 'twitter.com') return false

	const parts = url.pathname.split('/').filter(Boolean)
	if (parts.length !== 1) return false

	const handle = parts[0]
	if (!/^[A-Za-z0-9_]{1,15}$/.test(handle)) return false
	if (TWITTER_RESERVED_HANDLES.has(handle.toLowerCase())) return false
	return true
}

/** https://linkedin.com/in/username or https://www.linkedin.com/in/username */
export function isValidLinkedInProfileUrl(value) {
	const url = parseHttpsUrl(value)
	if (!url) return false

	const host = url.hostname.toLowerCase()
	const isLinkedInHost =
		host === 'linkedin.com' ||
		host === 'www.linkedin.com' ||
		/^[a-z]{2}\.linkedin\.com$/.test(host)
	if (!isLinkedInHost) return false

	const parts = url.pathname.split('/').filter(Boolean)
	if (parts.length !== 2 || parts[0].toLowerCase() !== 'in') return false

	const slug = parts[1]
	if (slug.length < 3 || slug.length > 100) return false
	if (!/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(slug)) return false
	return true
}

/**
 * Shared name / email / password (+ optional English name) checks.
 * `name` accepts Marathi, English, or both (with combining marks).
 */
export function validateAccountFields(
	{ name, nameEnglish, email, password } = {},
	{
		requireName = true,
		requireNameEnglish = false,
		requireEmail = true,
		requirePassword = false,
		maxNameLength = MAX_PERSON_NAME_LENGTH,
		maxEnglishNameLength = MAX_ENGLISH_NAME_LENGTH,
	} = {}
) {
	const nextName = normalizePersonName(name)
	const nextNameEnglish = normalizeEnglishName(nameEnglish)
	const nextEmail = normalizeEmail(email)
	const nextPassword = password == null ? '' : String(password)

	if (requireName) {
		if (!nextName) return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameRequired, field: 'name' }
		if (maxNameLength && nextName.length > maxNameLength) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameTooLong, field: 'name' }
		}
		if (!isValidPersonName(nextName)) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameFormat, field: 'name' }
		}
	} else if (nextName) {
		if (maxNameLength && nextName.length > maxNameLength) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameTooLong, field: 'name' }
		}
		if (!isValidPersonName(nextName)) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameFormat, field: 'name' }
		}
	}

	if (requireNameEnglish) {
		if (!nextNameEnglish) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameEnglishRequired, field: 'nameEnglish' }
		}
		if (maxEnglishNameLength && nextNameEnglish.length > maxEnglishNameLength) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameEnglishTooLong, field: 'nameEnglish' }
		}
		if (!isValidEnglishName(nextNameEnglish)) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameEnglishFormat, field: 'nameEnglish' }
		}
	} else if (nextNameEnglish) {
		if (maxEnglishNameLength && nextNameEnglish.length > maxEnglishNameLength) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameEnglishTooLong, field: 'nameEnglish' }
		}
		if (!isValidEnglishName(nextNameEnglish)) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.nameEnglishFormat, field: 'nameEnglish' }
		}
	}

	if (requireEmail) {
		if (!nextEmail) return { ok: false, error: ACCOUNT_FIELD_ERRORS.emailRequired, field: 'email' }
		if (!isValidEmail(nextEmail)) {
			return { ok: false, error: ACCOUNT_FIELD_ERRORS.emailFormat, field: 'email' }
		}
	} else if (nextEmail && !isValidEmail(nextEmail)) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.emailFormat, field: 'email' }
	}

	if (requirePassword) {
		const passwordError = getPasswordValidationError(nextPassword, { required: true })
		if (passwordError) {
			return { ok: false, error: passwordError, field: 'password' }
		}
	} else if (nextPassword) {
		const passwordError = getPasswordValidationError(nextPassword)
		if (passwordError) {
			return { ok: false, error: passwordError, field: 'password' }
		}
	}

	return {
		ok: true,
		values: {
			name: nextName,
			nameEnglish: nextNameEnglish || null,
			email: nextEmail || null,
			password: nextPassword || null,
		},
	}
}

/**
 * Full Author create/edit validation.
 * Name / designation / bio: Marathi or English or both; reject numbers-only / specials-only.
 * Experience: letters and numbers only (Marathi or English).
 */
export function validateAuthorFields(
	{
		name,
		nameEnglish,
		email,
		password,
		designation,
		experience,
		bio,
		twitter,
		linkedin,
	} = {},
	{ isEdit = false } = {}
) {
	const account = validateAccountFields(
		{ name, nameEnglish, email, password },
		{
			requireName: true,
			requireNameEnglish: true,
			requireEmail: true,
			requirePassword: !isEdit,
		}
	)
	if (!account.ok) return { ...account, field: account.field || 'name' }

	const nextDesignation = normalizeText(designation)
	const nextExperience = normalizeText(experience)
	const nextBio = normalizeText(bio)

	if (!nextDesignation) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.designationRequired, field: 'designation' }
	}
	if (nextDesignation.length > MAX_DESIGNATION_LENGTH) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.designationTooLong, field: 'designation' }
	}
	if (!isValidTextContent(nextDesignation, { min: 1, max: MAX_DESIGNATION_LENGTH })) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.designationFormat, field: 'designation' }
	}

	if (!nextExperience) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.experienceRequired, field: 'experience' }
	}
	if (nextExperience.length > MAX_EXPERIENCE_LENGTH) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.experienceTooLong, field: 'experience' }
	}
	if (!isValidExperience(nextExperience)) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.experienceFormat, field: 'experience' }
	}

	if (!nextBio) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.bioRequired, field: 'bio' }
	}
	if (!isValidTextContent(nextBio, { min: 1, max: MAX_TEXT_LENGTH })) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.bioFormat, field: 'bio' }
	}

	const twitterResult = normalizeUrl(twitter)
	if (twitterResult && !isValidTwitterProfileUrl(twitterResult)) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.twitterFormat, field: 'twitter' }
	}

	const linkedinResult = normalizeUrl(linkedin)
	if (linkedinResult && !isValidLinkedInProfileUrl(linkedinResult)) {
		return { ok: false, error: ACCOUNT_FIELD_ERRORS.linkedinFormat, field: 'linkedin' }
	}

	return {
		ok: true,
		values: {
			...account.values,
			designation: nextDesignation,
			experience: nextExperience,
			bio: nextBio,
			twitter: twitterResult || null,
			linkedin: linkedinResult || null,
		},
	}
}

/** Yup field schemas for Formik (Users and similar). Name allows Marathi or English. */
export function accountFieldsYupSchema({
	isEdit = false,
	emailRequired = true,
} = {}) {
	return {
		name: Yup.string()
			.trim()
			.required(ACCOUNT_FIELD_ERRORS.nameRequired)
			.max(MAX_PERSON_NAME_LENGTH, ACCOUNT_FIELD_ERRORS.nameTooLong)
			.test('valid-person-name', ACCOUNT_FIELD_ERRORS.nameFormat, (value) =>
				isValidPersonName(value)
			),
		email: emailRequired
			? Yup.string()
					.trim()
					.required(ACCOUNT_FIELD_ERRORS.emailRequired)
					.test('valid-email', ACCOUNT_FIELD_ERRORS.emailFormat, (value) =>
						isValidEmail(value)
					)
			: Yup.string()
					.trim()
					.test('valid-email', ACCOUNT_FIELD_ERRORS.emailFormat, (value) => {
						if (!value) return true
						return isValidEmail(value)
					}),
		password: Yup.string()
			.transform((value) => (value == null ? '' : String(value)))
			.test('password-rules', ACCOUNT_FIELD_ERRORS.passwordMinLength, function (value) {
				if (isEdit && !value) return true
				if (!value) {
					return this.createError({ message: ACCOUNT_FIELD_ERRORS.passwordRequired })
				}
				const error = getPasswordValidationError(value, { required: true })
				if (error) {
					return this.createError({ message: error })
				}
				return true
			}),
	}
}
